import { PaperSize } from "building-types/annotation/annotationTypesEnum";
import type { Building } from "building-types/building/buildingTypes";
import { StreetOrientationMode } from "building-types/building/streetOrientationMode";
import { FinishClipping } from "building-types/casing/casingTypes";
import {
  FoundationDatumVerticalAnchor,
  LevelDatumAnchor,
  WallLateralDatumAnchor,
} from "building-types/datums/datumsTypesEnum";
import {
  FoundationSubSystemType,
  FoundationSuperSystemType,
  SlabType,
} from "building-types/foundations/foundationEnums";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import { PaletteType } from "building-types/materials/paletteType";
import { OpeningWrappingModeEnum } from "building-types/openings/openingWrappingModes";
import type { RemoveFormula } from "building-types/options/formula";
import { ProfileExtrusionType } from "building-types/profiles/profileTypesEnum";
import { RoofWallFramingType } from "building-types/roofs/roofWallFramingTypes";
import { RoomLabelDimensionType, RoomLabelRounding, RoomLabelUnits } from "building-types/rooms/roomTypesEnum";
import { FloorAreaTakeoffType } from "building-types/rooms/takeoffTypes";
import type { AreaReportSettings } from "building-types/settings/areaReportSettings";
import type { AwningSettings } from "building-types/settings/awningSettings";
import type { CaseworkCatalogSettings } from "building-types/settings/caseworkCatalogSettings";
import type { CaseworkSettings } from "building-types/settings/caseworkSettings";
import type { ColumnCatalogSettings } from "building-types/settings/columnCatalogSettings";
import type { DoorCatalogSettings } from "building-types/settings/doorCatalogSettings";
import type { DoorSettings } from "building-types/settings/doorSettings";
import type { DoorWallFramingSettings } from "building-types/settings/doorWallFramingSettings";
import type { DoorWallWrappingSettings } from "building-types/settings/doorWallWrappingSettings";
import type { DormerSettings } from "building-types/settings/dormerSettings";
import type { DrawingCatalogSettings } from "building-types/settings/drawingCatalogSettings";
import type { ElectricalItemCatalogSettings } from "building-types/settings/electricalItemCatalogSettings";
import type { ExteriorDoorCasingSettings } from "building-types/settings/exteriorDoorCasingSettings";
import type { ExteriorPropSettings } from "building-types/settings/exteriorPropSettings";
import type { ExteriorTrimSettings } from "building-types/settings/exteriorTrimSettings";
import type { ExteriorWindowCasingSettings } from "building-types/settings/exteriorWindowCasingSettings";
import type { FloorSettings } from "building-types/settings/floorSettings";
import type { FoundationSettings } from "building-types/settings/foundationSettings";
import type {
  BasementFoundationDatumSettings,
  BasementFoundationSuperSystemSettings,
  CrawlspaceFoundationSuperSystemSettings,
  CurbSettings,
  FoundationDatumSettings,
  IsolatedFoundationSubSystemSettings,
  MonolithicFoundationSubSystemSettings,
  RaisedSlabFoundationSuperSystemSettings,
  SlabOnGradeFoundationSuperSystemSettings,
  SlabSettings,
  StemWallCIPFoundationSubSystemSettings,
  StemWallCMUFoundationSubSystemSettings,
} from "building-types/settings/foundationSettingsTypes";
import type { GarageSettings } from "building-types/settings/garageSettings";
import type { HeaderFramingSettings } from "building-types/settings/headerFramingSettings";
import type { HomeSettings } from "building-types/settings/homeSettings";
import type { InteriorDoorCasingSettings } from "building-types/settings/interiorDoorCasingSettings";
import type { InteriorPropSettings } from "building-types/settings/interiorPropSettings";
import type { InteriorTrimSettings } from "building-types/settings/interiorTrimSettings";
import type { InteriorWindowCasingSettings } from "building-types/settings/interiorWindowCasingSettings";
import type { KeyMeasuresSettings } from "building-types/settings/keyMeasuresSettings";
import type { MasterPlanSettings } from "building-types/settings/masterPlanSettings";
import { SheetCustomScales } from "building-types/settings/pageElevationTypes";
import type { PlanSettings } from "building-types/settings/planSettings";
import type { PorchSettings } from "building-types/settings/porchSettings";
import type { ProfileCatalogSettings } from "building-types/settings/profileCatalogSettings";
import type { RearPorchSettings } from "building-types/settings/rearPorchSettings";
import type { FriezeBoard } from "building-types/settings/roofBaseTypes";
import type { RoofFramingSettings } from "building-types/settings/roofFramingSettings";
import type { RoofElevationPlanSettings, RoofPlanSettings } from "building-types/settings/roofPlanSettings";
import type { RoofSettings } from "building-types/settings/roofSettings";
import type { ScheduleSettings } from "building-types/settings/scheduleSettings";
import { SettingsType } from "building-types/settings/settingsType";
import {
  ApronCasingType,
  AwningType,
  CaseworkDoorStyle,
  ColumnDimensionType,
  ConditionedAreaCalculationType,
  EaveType,
  ElevationNotePlacementType,
  ExteriorDimensionType,
  ExteriorWallOffsetMode,
  FasciaTopTrimSize,
  HeadCasingType,
  InteriorDimensionType,
  PlanLineStyle,
  PlanOrientation,
  ShutterType,
  SideCasingType,
  StoolCasingType,
  UtilityDimensionType,
  WainscotingCapType,
  WallRoofBaseBoardFilter,
  WallRoofBaseBoardMode,
  WindowMullionType,
} from "building-types/settings/settingsTypesEnum";
import { PlanSeries } from "building-types/settings/sheetsTypesEnum";
import type { StairCatalogSettings } from "building-types/settings/stairCatalogSettings";
import type { StructureSettings } from "building-types/settings/structureSettings";
import type { ViewNoteSettings } from "building-types/settings/viewNoteSettings";
import type { WainscotingSettings } from "building-types/settings/wainscotingSettings";
import type { WallCatalogSettings } from "building-types/settings/wallCatalogSettings";
import type { WallFramingOpeningSettings } from "building-types/settings/wallFramingOpeningSettings";
import type { WallFramingSettings } from "building-types/settings/wallFramingSettings";
import type { WallSettings } from "building-types/settings/wallSettings";
import type { WindowCatalogSettings } from "building-types/settings/windowCatalogSettings";
import type { WindowMaterialSettings } from "building-types/settings/windowMaterialSettings";
import type { WindowMullionSettings } from "building-types/settings/windowMullionSettings";
import type { WindowSettings } from "building-types/settings/windowSettings";
import type { WindowShutterSettings } from "building-types/settings/windowShutterSettings";
import { GlazingColor, GlazingType, WindowPanelType } from "building-types/settings/windowTypesEnum";
import type { WindowWallFramingSettings } from "building-types/settings/windowWallFramingSettings";
import type { WindowWallWrappingSettings } from "building-types/settings/windowWallWrappingSettings";
import { ViewEntityType } from "building-types/views/viewEntityType";
import { WALL_OFFSET_INTERIOR } from "common/constants/buildingObjects";
import { worldInchesNew } from "common/utils/nominalNumbers";
import {
  defaultDoubleHeaderAssemblyId2x10,
  defaultDoubleHeaderAssemblyId2x12,
  defaultDoubleHeaderAssemblyId2x4,
  defaultDoubleHeaderAssemblyId2x6,
  defaultDoubleHeaderAssemblyId2x8,
} from "doc/elements/framing/defaultHeaderAssembly";
import { defaultCaseworkTypes } from "../defaults/caseworkDefaults";
import { defaultFrameFloorAssemblyId } from "../defaults/defaultFloorAssembly";
import {
  DOORS_SCHEDULE_DEFAULT_SETTINGS,
  WINDOWS_SCHEDULE_DEFAULT_SETTINGS,
  ROOMS_SCHEDULE_DEFAULT_SETTINGS,
  CASEWORK_SCHEDULE_DEFAULT_SETTINGS,
} from "../defaults/measureSettingsScheduleDefaults";
import { defaultDoorTypes } from "../elements/doors/doorCatalog";
import { defaultLoadCatalog } from "../elements/electrical/electricalTools";
import { defaultFoundationAssemblyId } from "../elements/foundations/defaultFoundationAssemblies";
import { defaultExteriorPropCatalog } from "../elements/propCatalog/exteriorPropCatalog";
import { defaultInteriorPropCatalog } from "../elements/propCatalog/interiorPropCatalog";
import { defaultRailingSettings } from "../elements/railing/constants";
import { defaultStairCatalogSettings } from "../elements/stairSystem/stairConstants";
import { defaultWindowTypes } from "../elements/window/windowCatalog";
import { fasciaTopTrimSizeToValue } from "../fasciaTopTrimSizeToValue";
import { defaultDrawingCatalog } from "../plans/drawingCatalog";
import { headCasingTrimNew } from "../settingsTools";
import { defaultColumnsInternalIds } from "../utils/columnDescriptorUtils";
import { floorAreaTakeoffName } from "../utils/floorAreaTakeoffTools";
import { defaultWallMaterials } from "../utils/wallMaterialTools";
import { defaultWallConditions, defaultWallTypes } from "../utils/wallTypeTools";
import { defaultElectricalSettings } from "./builtinElectricalSettings";
import { BuiltinHomeSettingsId } from "./builtinHomeSettingsId";
import { defaultExteriorMaterialSettings } from "./defaultExteriorMaterials";
import {
  defaultAllExteriorTrimViewNotes,
  defaultAwningViewNotes,
  defaultExteriorDoorCasingViewNotes,
  defaultExteriorWindowCasingViewNotes,
  defaultPorchViewNotes,
  defaultShutterViewNotes,
  defaultWainscotingViewNotes,
} from "./defaultViewNoteSettings";
import { defaultInteriorMaterialSettings } from "./interiorMaterialSettingsLibrary";

export const BASE_SETTINGS_SETS_NAME = "Base Settings Set";

export const defaultShedAwningSettings: AwningSettings = {
  type: SettingsType.AWNING_SETTINGS,
  awningType: AwningType.SHED,
  bracketTrimSpacing: 6,
  fasciaHeight: 6,
  bracketOffset: 4,
  slope: 1,
  depth: 24,
  trimHeight: 4,
  trimWidth: 1,
  awningWidthOffset: 4,
  awningHeightOffset: 0,
  roofMaterial: "roof0",
  soffit: false,
  soffitHeight: 6,
  soffitDepthOffset: 2,
  soffitWidthOffset: 2,
  viewNotes: defaultAwningViewNotes,
};

export const defaultAwningSettings: AwningSettings = {
  ...defaultShedAwningSettings,
  awningType: AwningType.DISABLED,
  viewNotes: defaultAwningViewNotes,
};

//override these as necessary here, or in each palette type
export const sharedSettingsDefaults = {
  roofShedOverhang0: 16,
};

export const defaultWainscotingSettings: WainscotingSettings = {
  type: SettingsType.WAINSCOTING_SETTINGS,
  height: 32,
  thickness: 2.0,
  wainscotingMode: StreetOrientationMode.ALL_FACES,
  waincotingDatum: LevelDatumAnchor.LEVEL_FLOOR_ELEVATION,
  closeDist: 1,
  removeWainscotingSegmentsUnderLength: 3,
  capType: WainscotingCapType.CAP,
  capHeight: 2,
  capThickness: 1,
  leftThreshold: 500,
  rightThreshold: 500,
  lateralThreshold: 500,
  viewNotes: defaultWainscotingViewNotes,
  showWainscotingOnPlans: true,
  porchWainscotingMode: StreetOrientationMode.NO_FACES,
  porchWainscotingDatum: FoundationDatumVerticalAnchor.TOP_OF_SLAB,
};

export const defaultWallFramingOpeningSettings: WallFramingOpeningSettings = {
  hasCrippleStudEnds: true,
  hasCrippleStudsBelowOpening: true,
  hasCrippleStudsAboveOpening: true,
  hasTopHeaderSillPlate: false,
  numberOfKingStuds: 1,
  roughOpeningBorder: 1,
};

export const defaultWindowFramingSettings: WindowWallFramingSettings = {
  type: SettingsType.WINDOW_FRAMING_SETTINGS,
  ...defaultWallFramingOpeningSettings,
};

export const defaultDoorFramingSettings: DoorWallFramingSettings = {
  type: SettingsType.DOOR_FRAMING_SETTINGS,
  ...defaultWallFramingOpeningSettings,
};

export const defaultWallFramingSettings: WallFramingSettings = {
  type: SettingsType.WALL_FRAMING_SETTINGS,
  hasDoubleTopPlate: true,
  hasBlocking: false,
  studDistance: 16,
};

export const defaultWindowWrappingSettings: WindowWallWrappingSettings = {
  type: SettingsType.WINDOW_WRAPPING_SETTINGS,
  wrappingMode: OpeningWrappingModeEnum.NONE,
};

export const defaultDoorWrappingSettings: DoorWallWrappingSettings = {
  type: SettingsType.DOOR_WRAPPING_SETTINGS,
  wrappingMode: OpeningWrappingModeEnum.NONE,
};

export const defaultExteriorWindowCasingSettings: ExteriorWindowCasingSettings = {
  type: SettingsType.EXTERIOR_WINDOW_CASING_SETTINGS,
  factoryMulled: true,
  casingHorizontalOffset: 0,
  casingLateralOffset: 0,
  stoolCasingType: StoolCasingType.TRIM,
  headCasingFinishClipping: FinishClipping.NO_CLIPPING,
  sideCasingFinishClipping: FinishClipping.CLIP_FINISH,
  apronCasingFinishClipping: FinishClipping.NO_CLIPPING,
  stoolCasingFinishClipping: FinishClipping.CLIP_FINISH,
  stoolCasingDepth: 1.5,
  stoolCasingThickness: 1.5,
  stoolCasingExtension: 0,
  stoolCasingMaterial: "trimExterior0",
  apronCasingType: ApronCasingType.TRIM,
  apronHeight: 4,
  apronDepth: 0.75,
  apronExtension: 0,
  apronEndSlope: 0,
  sideCasingType: SideCasingType.TRIM,
  sideCasingWidth: 3.5,
  sideCasingDepth: 0.75,
  sideCasingTopOffset: 0,
  sideCasingBottomOffset: 0,
  ...headCasingTrimNew(),
  viewNotes: defaultExteriorWindowCasingViewNotes,
};

export const defaultWindowMullionSettings: WindowMullionSettings = {
  type: SettingsType.WINDOW_MULLION_SETTINGS,
  mullionType: WindowMullionType.ORTHOGONAL,
  paneOneXCount: 2,
  paneOneYCount: 2,
  paneTwoYCount: 0,
  paneTwoXCount: 0,
  verticalThickness: 1,
  horizontalThickness: 1,
  horizontalSplit: false,
};

export const defaultWindowShutterSettings: WindowShutterSettings = {
  type: SettingsType.WINDOW_SHUTTER_SETTINGS,
  shutterType: ShutterType.DISABLED,
  viewNotes: defaultShutterViewNotes,
};

type WindowTrimSettings = Pick<HomeSettings, "windowMullions" | "windowShutters" | "exteriorWindowCasing">;

export const defaultWindowTrimSettings: WindowTrimSettings = {
  exteriorWindowCasing: defaultExteriorWindowCasingSettings,
  windowMullions: defaultWindowMullionSettings,
  windowShutters: defaultWindowShutterSettings,
};

export const defaultFarmhouseWindowSettings: WindowTrimSettings = {
  ...defaultWindowTrimSettings,
  windowMullions: {
    type: SettingsType.WINDOW_MULLION_SETTINGS,
    mullionType: WindowMullionType.ORTHOGONAL,
    paneOneXCount: 2,
    paneOneYCount: 2,
    paneTwoYCount: 0,
    paneTwoXCount: 0,
    verticalThickness: 1,
    horizontalThickness: 1,
    horizontalSplit: false,
  },
};

export const defaultPorchSettings: PorchSettings = {
  type: SettingsType.PORCH_SETTINGS,
  maxColumnSpacing: 120,
  colonnadeTerminationRules: [],
  includeRailing: false,
  baseColumnOffset: false,
  internalColumnStructureSize: 3.5,
  internalHeaderStructureWidth: 3.5,
  headerCasingOffsetFromCenterLine: 1.375,
  headerCasingThickness: 8.25,
  columnCasingOffset: 0,
  removeHeader: false,
  removeColumns: false,
  columnId: "DEFAULT",
  viewNotes: defaultPorchViewNotes,
};

export const defaultRearPorchSettings: RearPorchSettings = {
  type: SettingsType.REAR_PORCH_SETTINGS,
  maxColumnSpacing: 120,
  colonnadeTerminationRules: [],
  includeRailing: false,
  baseColumnOffset: false,
  internalColumnStructureSize: 3.5,
  internalHeaderStructureWidth: 3.5,
  headerCasingOffsetFromCenterLine: 1.375,
  headerCasingThickness: 8.25,
  columnCasingOffset: 0,
  removeHeader: false,
  removeColumns: false,
  columnId: "DEFAULT",
  viewNotes: defaultPorchViewNotes,
};

export const defaultGarageSettings: GarageSettings = {
  type: SettingsType.GARAGE_SETTINGS,
  drivewayDoorOffset: 12,
  sideDrivewayMargin: 24,
};

const defaultKeyMeasuresSettings: KeyMeasuresSettings = {
  type: SettingsType.KEY_MEASURES_SETTINGS,
  includeCasework: false,
};

export const defaultExteriorTrimSettings: ExteriorTrimSettings = {
  type: SettingsType.EXTERIOR_TRIM_SETTINGS,
  enableOutsideCornerMoulding: true,
  enableInsideCornerMoulding: false,
  wallRoofBaseBoard: WallRoofBaseBoardMode.NONE,
  wallRoofBaseBoardFilter: WallRoofBaseBoardFilter.ALL_EDGES,
  viewNotes: defaultAllExteriorTrimViewNotes,
};

export const defaultInteriorTrimSettings: InteriorTrimSettings = {
  type: SettingsType.INTERIOR_TRIM_SETTINGS,
  baseBoardMaterial: "trimInterior0",
  crownMoldingMaterial: "trimInterior0",
};

const defaultStructureSettings: StructureSettings = {
  type: SettingsType.STRUCTURE_SETTINGS,
  maxUnsupportedCornerSpan: 480,
};

export const defaultAreaReportSettings = Object.values(FloorAreaTakeoffType).reduce((settings, type) => {
  const commonDefaults = {
    reportedName: floorAreaTakeoffName(type),
    areaCalculationType: ConditionedAreaCalculationType.BY_ROOM_TYPE,
    stairAreaFirstFloorOnly: false,
  };

  switch (type) {
    case FloorAreaTakeoffType.CONDITIONED_FLOOR_AREA:
      settings[type] = {
        ...commonDefaults,
        enabled: true,
        showByLevel: true,
        showTotals: true,
      };
      break;
    case FloorAreaTakeoffType.UNCONDITIONED_FLOOR_AREA:
      settings[type] = {
        ...commonDefaults,
        enabled: false,
        showByLevel: true,
        showTotals: true,
      };
      break;
    default:
      settings[type] = {
        ...commonDefaults,
        enabled: true,
        showByLevel: false,
        showTotals: false,
      };
      break;
  }
  return settings;
}, {} as AreaReportSettings);

export const defaultRoofElevationPlanSettings: RoofElevationPlanSettings = {
  showRoofHorizontalPlacementDims: false,
  showRoofGableWindowVerticalDims: true,
  showRoofGableWindowReportedName: true,
  showRoofTruncationHeightDims: true,
  showRoofWidthDims: false,
  showRoofSlopeElevationAnnotation: true,

  showDormerWindowVerticalDimensions: true,
  showDormerWindowReportedName: true,
  showDormerWallFramingHeightDimensions: false,
  showDormerWallFramingWidthDimensions: false,
  showDormerGableWindowVerticalDims: true,
  showDormerGableWindowReportedName: true,
  showDormerRoofTruncationHeightDims: true,
  showDormerRoofWidthDims: true,
  showDormerHorizontalPlacementDims: false,
  showDormerRoofSlopeElevationAnnotation: true,
};

export const defaultRoofPlanSettings: RoofPlanSettings = {
  includeRoofOverhangDimensions: true,
  includeRoofSlopeAnnotation: true,

  includeDormerOverhangDimensions: false,
  includeDormerRoofSlopeAnnotation: true,
  includeDormerDashedFraming: false,
};

export const defaultFriezeBoardSettings: FriezeBoard[] = [
  { friezeBoard: ProfileExtrusionType.DEFAULT_PROFILE, name: "Default Frieze Board" },
];

export const defaultRoofAnnotationSettings = {
  ...defaultRoofPlanSettings,
  ...defaultRoofElevationPlanSettings,
};

export const defaultPlanSettings: PlanSettings = {
  type: SettingsType.PLAN_SETTINGS,
  paperSize: PaperSize.SHEET_22X34,
  annotationScale: 1,
  includeSchedules: true,
  includeFloorPlans: true,
  includeRoofPlans: true,
  includeFrontBackElevations: true,
  includeLeftRightElevations: true,
  includeSections: true,
  includePerspectiveViews: true,
  includeElectricPlans: true,
  includeFoundationPlans: true,
  includeFoundationDetails: true,
  includeStructuralPlans: false,
  includeUtilityPlans: false,
  includeDesignCriteria: true,
  areaReportSettings: defaultAreaReportSettings,
  includeOptionsSchedule: true,
  includeGarageDoorsInDoorSchedule: false,
  enableAutogradeLine: true,
  overlayFoundationOutlines: false,
  showWindowHeaderDimsOnElevations: true,
  automatedDatumAnnotations: true,
  belowGradeRenderStyle: PlanLineStyle.NONE,
  includeFloorMaterialsDiagram: false,
  includeConditionedAreaDiagram: true,
  includeBasementFloorPlan: true,
  includeSliderOperation: false,
  minWallLength: 60,
  minJambLength: 8,
  archPlanSeries: PlanSeries.A,
  interiorDimensionType: InteriorDimensionType.BY_STRING,
  interiorDimensionAreaThreshold: 0,
  exteriorDimensionType: ExteriorDimensionType.THREE_ROW,
  exteriorDimensionDatum: WallLateralDatumAnchor.OUTSIDE_FACE_OF_FRAMING,
  exteriorDimensionOffsetMultiplier: 1,
  exteriorOpeningTagOffset: 8,
  interiorOpeningTagOffset: 5,
  addCeilingHeightToRoomTag: false,
  addFloorMaterialToRoomTag: false,
  columnDimensionType: ColumnDimensionType.HYBRID,
  includeWindowTagExtensions: false,
  orientation: PlanOrientation.DEFAULT,
  utilityDimensionType: UtilityDimensionType.OUTSIDE_EDGE_OF_SLAB,
  excludeNonHouseSlabEdges: false,
  includeFootingInfo: true,
  includeDetailCallouts: true,
  dimensionPlumbingProps: false,
  dimensionElectricalFloorProps: false,
  dimensionBrickLedge: true,
  dimensionInteriorFootings: true,
  dimensionInteriorWallTJunctionsOnExteriorDims: true,
  elevationNotePlacementType: ElevationNotePlacementType.IN_PLACE,
  elevationScaleOverride: SheetCustomScales.SCALE_AUTO,
  enableOpenToAboveAnnotation: false,
  includeExcavationNote: true,
  includePorchColumnStructure: true,
  includePorchHeaderStructure: true,
  roomLabelUnits: RoomLabelUnits.WHOLE_FEET,
  roomLabelRounding: RoomLabelRounding.LOWEST,
  roomLabelDimensionType: RoomLabelDimensionType.ROOM_DECOMPOSITION,
  generateFramingPlan: false,
  labelFramingElements: false,
  enableLegacyFontScaling: false,
  ...defaultRoofAnnotationSettings,
};

export const defaultMasterPlanSettings: MasterPlanSettings = {
  type: SettingsType.MASTER_PLAN_SETTINGS,
  includeCoverPage: false,
  includeSchedules: true,
  includeFloorPlans: true,
  includeBasementFloorPlan: true,
  includeRoofPlans: true,
  includeFrontBackElevations: true,
  includeLeftRightElevations: true,
  includeSections: false,
  includePerspectiveViews: false,
  includeElectricPlans: true,
  includeFoundationPlans: true,
  includeFoundationDetails: false,
  includeStructuralPlans: false,
  includeUtilityPlans: false,
};

export const defaultExteriorPropSettings: ExteriorPropSettings = {
  type: SettingsType.EXTERIOR_PROP_SETTINGS,
  propTypes: defaultExteriorPropCatalog,
};

export const defaultInteriorPropSettings: InteriorPropSettings = {
  type: SettingsType.INTERIOR_PROP_SETTINGS,
  propTypes: defaultInteriorPropCatalog,
};

export const DEFAULT_WALL_THICKNESS_STRUCTURE = 3.5;
const DEFAULT_WALL_THICKNESS_SUBSTRATE = 0.5;
export const DEFAULT_EAVE_RETURN_DISTANCE = 6;
export const DEFAULT_EAVE_EXTENSION_AT_COPLANAR_RAKE = DEFAULT_EAVE_RETURN_DISTANCE;
export const DEFAULT_ROOFING_CAP_THICKNESS = 1 / 2;
export const DEFAULT_ROOFING_CAP_WIDTH = 6;
export const ARBITRARY_ROOFING_CAP_EXTENSION = 12;
export const RIDGE_CLIPPING_ORIGIN_RIDGE_END_TOLERANCE = 1;

const defaultWallSettings: WallSettings = {
  type: SettingsType.WALL_SETTINGS,
  exteriorWallBaseThickness: DEFAULT_WALL_THICKNESS_STRUCTURE,
  exteriorWallSubstrateThickness: DEFAULT_WALL_THICKNESS_SUBSTRATE,
  interiorWallOffset: WALL_OFFSET_INTERIOR,
  exteriorWallOffset: DEFAULT_WALL_THICKNESS_STRUCTURE - WALL_OFFSET_INTERIOR,
  exteriorWallMaterial: "wallExterior0",
  exteriorWallOffsetMode: ExteriorWallOffsetMode.OUTSIDE_THICKENING,
  exteriorWallOffsetDatum: WallLateralDatumAnchor.OUTSIDE_FACE_OF_FRAMING,
  wallConditions: defaultWallConditions,
  insideCornerWallOffsetForInteriorWallThickening: DEFAULT_WALL_THICKNESS_STRUCTURE - WALL_OFFSET_INTERIOR,
  insideCornerWallOffsetForInteriorRoofWallThickening: WALL_OFFSET_INTERIOR,
  outsideCornerWallOffsetForInteriorWallThickening: DEFAULT_WALL_THICKNESS_STRUCTURE / 2,
};

export function buildingGetWallSettings(building: Building): WallSettings {
  return building?.settings.wall ?? defaultWallSettings;
}

export const defaultWallCatalogSettings: WallCatalogSettings = {
  type: SettingsType.WALL_CATALOG_SETTINGS,
  wallTypes: defaultWallTypes,
  wallMaterials: defaultWallMaterials,
};

export function buildingGetWallCatalogSettings(building: Building): WallCatalogSettings {
  return building?.settings?.wallCatalog ?? defaultWallCatalogSettings;
}

export function buildingGetStairCatalogSettings(building: Building): StairCatalogSettings {
  return building?.settings?.stair ?? defaultStairCatalogSettings;
}

export const defaultDoorSettings: DoorSettings = {
  type: SettingsType.DOOR_SETTINGS,
  interiorDoorHeight: 80,
};

export const defaultWindowSettings: WindowSettings = {
  type: SettingsType.WINDOW_SETTINGS,
  windowHeadHeight: 96,
  verticalDivisionSpacing: 3,
};

export const defaultRoofSettings: RemoveFormula<RoofSettings> = {
  type: SettingsType.ROOF_SETTINGS,
  eaveOverhang: 12,
  rakeOverhang: 12,
  eaveReturnDistance: 6,
  eaveExtensionAtCoplanarRake: 6,
  eaveExtensionAtNonCoplanarRake: 0,
  eaveExtensionAtCoplanarWall: 1.75,
  rakeExtensionAtCoplanarWall: 0,
  shedOverhang: sharedSettingsDefaults.roofShedOverhang0,
  eaveType: EaveType.BOXED_BOX,
  includeFasciaTopTrim: true,
  fasciaTopTrimSize: fasciaTopTrimSizeToValue[FasciaTopTrimSize.TWO_INCH],
  eaveFasciaSize: 8,
  rakeFasciaSize: 8,
  rakeFriezeBoard: defaultFriezeBoardSettings,
  includeFriezeBoardOnPorches: true,
};

export const defaultDormerSettings: DormerSettings = {
  type: SettingsType.DORMER_SETTINGS,
  eaveFriezeBoard: ProfileExtrusionType.NONE,
};

export const defaultRoofFramingSettings: RoofFramingSettings = {
  type: SettingsType.ROOF_FRAMING_SETTINGS,
  startFramingType: RoofWallFramingType.AUTO,
  endFramingType: RoofWallFramingType.AUTO,
  eaveFramingType: RoofWallFramingType.AUTO,
};

const defaultViewNoteSettings: ViewNoteSettings = {
  type: SettingsType.VIEW_NOTE_SETTINGS,
  typicalSuffix: "TYP",
  [ViewEntityType.NONE]: { enabled: false },
  [ViewEntityType.FASCIA]: { enabled: true, text: "FASCIA", tagOnlyOne: true },
  [ViewEntityType.EAVE_FASCIA]: { enabled: false, text: "EAVE FASCIA" },
  [ViewEntityType.EAVE_SOFFIT]: { enabled: false, text: "EAVE SOFFIT" },
  [ViewEntityType.FASCIA_TRIM]: { enabled: true, text: "FASCIA TRIM", tagOnlyOne: true },
  [ViewEntityType.RAFTER_EXTENSION]: { enabled: false, text: "RAFTER EXTENSION" },
  [ViewEntityType.BOX_EAVE]: { enabled: false, text: "BOX EAVE" },
  [ViewEntityType.MINI_HIP_EAVE]: { enabled: false, text: "MINI HIP EAVE" },
  [ViewEntityType.MINI_HIP_ROOF]: { enabled: false, text: "MINI HIP ROOF" },
  [ViewEntityType.MINI_SHED_EAVE]: { enabled: false, text: "MINI SHED EAVE" },
  [ViewEntityType.MINI_SHED_ROOF]: { enabled: false, text: "MINI SHED ROOF" },
  [ViewEntityType.GABLE_ROOF_VENT]: { enabled: false, text: "GABLE ROOF VENT" },
  [ViewEntityType.WINDOW_EXTERIOR_TRIM]: { enabled: false, text: "WINDOW EXTERIOR" },
  [ViewEntityType.DOOR_EXTERIOR]: { enabled: false, text: "DOOR EXTERIOR TRIM" },
  [ViewEntityType.PROFILE_EXTRUSION]: { enabled: false }, // profile extrusions are labeled elsewhere
  [ViewEntityType.COLUMN]: { enabled: false }, // columns are labeled elsewhere
  [ViewEntityType.RAILING]: { enabled: false, text: "RAILING" },
};

export const defaultProfileSettings: ProfileCatalogSettings = {
  type: SettingsType.PROFILE_CATALOG_SETTINGS,
  profileTable: {},
};

export const defaultColumnSettings: ColumnCatalogSettings = {
  type: SettingsType.COLUMN_CATALOG_SETTINGS,
  columnTable: defaultColumnsInternalIds,
};

export const defaultExteriorDoorCasingSettings: ExteriorDoorCasingSettings = {
  type: SettingsType.EXTERIOR_DOOR_CASING_SETTINGS,
  casingHorizontalOffset: 0,
  casingLateralOffset: 0,
  headCasingFinishClipping: FinishClipping.NO_CLIPPING,
  apronCasingFinishClipping: FinishClipping.CLIP_FINISH,
  sideCasingFinishClipping: FinishClipping.CLIP_FINISH,
  stoolCasingFinishClipping: FinishClipping.CLIP_FINISH,
  headCasingType: HeadCasingType.TRIM,
  headCasingHeight: 3.5,
  headCasingDepth: 1,
  apronCasingType: ApronCasingType.DISABLED,
  apronDepth: 0.75,
  sideCasingType: SideCasingType.TRIM,
  sideCasingWidth: 3,
  sideCasingDepth: 0.75,
  sideCasingTopOffset: 0,
  sideCasingBottomOffset: 0,
  sideCasingMaterial: "sideCasing0",
  viewNotes: defaultExteriorDoorCasingViewNotes,
};

export const defaultInteriorDoorCasingSettings: InteriorDoorCasingSettings = {
  type: SettingsType.INTERIOR_DOOR_CASING_SETTINGS,
  headMaterial: "trimInterior0",
  headExtension: 0,
  sideMaterial: "trimInterior0",
};

export const defaultInteriorWindowCasingSettings: InteriorWindowCasingSettings = {
  type: SettingsType.INTERIOR_WINDOW_CASING_SETTINGS,
  includeCasing: true,
};

export const noTrimExteriorDoorCasingSettings: ExteriorDoorCasingSettings = {
  type: SettingsType.EXTERIOR_DOOR_CASING_SETTINGS,
  casingHorizontalOffset: 0,
  casingLateralOffset: 0,
  headCasingType: HeadCasingType.NONE,
  apronCasingType: ApronCasingType.DISABLED,
  sideCasingType: SideCasingType.DISABLED,
  viewNotes: defaultExteriorDoorCasingViewNotes,
};

const defaultCurbSettings: CurbSettings = {
  garageCurbWidth: 6,
};

const defaultMonolithicSubSystemSettings: MonolithicFoundationSubSystemSettings = {
  foundationSubSystemType: FoundationSubSystemType.MONOLITHIC,
  foundationAssembly: defaultFoundationAssemblyId(FoundationSubSystemType.MONOLITHIC),
  ...defaultCurbSettings,
};

const defaultStemWallCIPSubSystemSettings: StemWallCIPFoundationSubSystemSettings = {
  foundationSubSystemType: FoundationSubSystemType.STEM_WALL_CIP,
  foundationAssembly: defaultFoundationAssemblyId(FoundationSubSystemType.STEM_WALL_CIP),
};

const defaultStemWallCMUSubSystemSettings: StemWallCMUFoundationSubSystemSettings = {
  foundationSubSystemType: FoundationSubSystemType.STEM_WALL_CMU,
  foundationAssembly: defaultFoundationAssemblyId(FoundationSubSystemType.STEM_WALL_CMU),
};

const defaultConcretePierSubSystemSettings: IsolatedFoundationSubSystemSettings = {
  foundationSubSystemType: FoundationSubSystemType.ISOLATED,
  foundationAssembly: defaultFoundationAssemblyId(FoundationSubSystemType.ISOLATED),
};

const defaultFullSlabSettings: SlabSettings = {
  interiorSlabType: SlabType.CIP,
  interiorSlabDepth: 6,
  garageSlabType: SlabType.CIP,
  garageSlabDepth: 6,
  porchSlabType: SlabType.CIP,
  porchSlabDepth: 6,
  basementSlabType: SlabType.NONE,
  porchSlabOverhang: 0,
};

const defaultSlabOnGradeDatumSettings: FoundationDatumSettings = {
  interiorDatumOffset: 6,
  garageDatum: LevelDatumAnchor.LEVEL_FLOOR_ELEVATION,
  garageDatumOffset: -6,
  porchDatum: LevelDatumAnchor.LEVEL_FLOOR_ELEVATION,
  porchDatumOffset: -4,
  deckDatum: LevelDatumAnchor.LEVEL_FLOOR_ELEVATION,
  deckDatumOffset: -4,
  brickLedgeDatum: LevelDatumAnchor.LEVEL_FLOOR_ELEVATION,
  brickLedgeDatumOffset: -7,
  foundationDatum: LevelDatumAnchor.LEVEL_FLOOR_ELEVATION,
  foundationDatumOffset: 36,
  foundationPerimeterDatum: WallLateralDatumAnchor.OUTSIDE_FACE_OF_FRAMING,
  foundationPerimeterDatumOffset: 0,
};

const defaultSlabOnGradeSuperSystemSettings: SlabOnGradeFoundationSuperSystemSettings & FoundationDatumSettings = {
  foundationSuperSystemType: FoundationSuperSystemType.SLAB_ON_GRADE,
  ...defaultFullSlabSettings,
  ...defaultSlabOnGradeDatumSettings,
};

const defaultRaisedSlabDatumSettings: FoundationDatumSettings = {
  interiorDatumOffset: 48,
  garageDatum: LevelDatumAnchor.LEVEL_GRADE,
  garageDatumOffset: 1,
  porchDatum: LevelDatumAnchor.LEVEL_FLOOR_ELEVATION,
  porchDatumOffset: -4,
  deckDatum: LevelDatumAnchor.LEVEL_FLOOR_ELEVATION,
  deckDatumOffset: -4,
  brickLedgeDatum: LevelDatumAnchor.LEVEL_GRADE,
  brickLedgeDatumOffset: -7,
  foundationDatum: LevelDatumAnchor.LEVEL_GRADE,
  foundationDatumOffset: 36,
  foundationPerimeterDatum: WallLateralDatumAnchor.OUTSIDE_FACE_OF_FRAMING,
  foundationPerimeterDatumOffset: 0,
};

const defaultRaisedSlabSuperSystemSettings: RaisedSlabFoundationSuperSystemSettings & FoundationDatumSettings = {
  foundationSuperSystemType: FoundationSuperSystemType.RAISED_SLAB,
  ...defaultFullSlabSettings,
  ...defaultRaisedSlabDatumSettings,
};

const defaultFloorSystemSlabSettings: SlabSettings = {
  interiorSlabType: SlabType.NONE,
  garageSlabType: SlabType.CIP,
  garageSlabDepth: 6,
  porchSlabType: SlabType.CIP,
  porchSlabDepth: 6,
  basementSlabType: SlabType.CIP,
  basementSlabDepth: 6,
  porchSlabOverhang: 0,
};

const defaultBasementDatumSettings: FoundationDatumSettings & BasementFoundationDatumSettings = {
  interiorDatumOffset: 48,
  garageDatum: LevelDatumAnchor.LEVEL_GRADE,
  garageDatumOffset: 1,
  porchDatum: LevelDatumAnchor.LEVEL_FLOOR_ELEVATION,
  porchDatumOffset: -4,
  deckDatum: LevelDatumAnchor.LEVEL_FLOOR_ELEVATION,
  deckDatumOffset: -4,
  brickLedgeDatum: LevelDatumAnchor.LEVEL_GRADE,
  brickLedgeDatumOffset: -7,
  foundationDatum: LevelDatumAnchor.LEVEL_GRADE,
  foundationDatumOffset: 36,
  foundationPerimeterDatum: WallLateralDatumAnchor.OUTSIDE_FACE_OF_FRAMING,
  foundationPerimeterDatumOffset: 0,
  basementFoundationDatum: LevelDatumAnchor.LEVEL_FLOOR_ELEVATION,
  basementFoundationDatumOffset: 14,
};

const defaultBasementSuperSystemSettings: BasementFoundationSuperSystemSettings & FoundationDatumSettings = {
  foundationSuperSystemType: FoundationSuperSystemType.BASEMENT,
  ...defaultFloorSystemSlabSettings,
  ...defaultBasementDatumSettings,
};

const defaultCrawlspaceSlabSettings: SlabSettings = {
  interiorSlabType: SlabType.NONE,
  garageSlabType: SlabType.CIP,
  garageSlabDepth: 6,
  porchSlabType: SlabType.CIP,
  porchSlabDepth: 6,
  basementSlabType: SlabType.NONE,
  porchSlabOverhang: 0,
};

const defaultCrawlspaceSuperSystemSettings: CrawlspaceFoundationSuperSystemSettings & FoundationDatumSettings = {
  foundationSuperSystemType: FoundationSuperSystemType.CRAWLSPACE,
  ...defaultCrawlspaceSlabSettings,
  ...defaultRaisedSlabDatumSettings,
};

const defaultFoundationSettings: FoundationSettings = {
  type: SettingsType.FOUNDATION_SETTINGS,
  foundationHash: "SLAB_ON_GRADE.MONOLITHIC",
  "SLAB_ON_GRADE.MONOLITHIC": {
    id: "SLAB_ON_GRADE.MONOLITHIC",
    ...defaultSlabOnGradeSuperSystemSettings,
    ...defaultMonolithicSubSystemSettings,
    foundationConditions: {},
  },
  "SLAB_ON_GRADE.STEM_WALL_CMU": {
    id: "SLAB_ON_GRADE.STEM_WALL_CMU",
    ...defaultSlabOnGradeSuperSystemSettings,
    ...defaultStemWallCMUSubSystemSettings,
    foundationConditions: {},
  },
  "SLAB_ON_GRADE.STEM_WALL_CIP": {
    id: "SLAB_ON_GRADE.STEM_WALL_CIP",
    ...defaultSlabOnGradeSuperSystemSettings,
    ...defaultStemWallCIPSubSystemSettings,
    foundationConditions: {},
  },
  "RAISED_SLAB.STEM_WALL_CMU": {
    id: "RAISED_SLAB.STEM_WALL_CMU",
    ...defaultRaisedSlabSuperSystemSettings,
    ...defaultStemWallCMUSubSystemSettings,
    foundationConditions: {},
  },
  "RAISED_SLAB.STEM_WALL_CIP": {
    id: "RAISED_SLAB.STEM_WALL_CIP",
    ...defaultRaisedSlabSuperSystemSettings,
    ...defaultStemWallCIPSubSystemSettings,
    foundationConditions: {},
  },
  "CRAWLSPACE.STEM_WALL_CMU": {
    id: "CRAWLSPACE.STEM_WALL_CMU",
    ...defaultCrawlspaceSuperSystemSettings,
    ...defaultStemWallCMUSubSystemSettings,
    foundationConditions: {},
  },
  "CRAWLSPACE.STEM_WALL_CIP": {
    id: "CRAWLSPACE.STEM_WALL_CIP",
    ...defaultCrawlspaceSuperSystemSettings,
    ...defaultStemWallCIPSubSystemSettings,
    foundationConditions: {},
  },
  "BASEMENT.STEM_WALL_CMU": {
    id: "BASEMENT.STEM_WALL_CMU",
    ...defaultBasementSuperSystemSettings,
    ...defaultStemWallCMUSubSystemSettings,
    foundationConditions: {},
  },
  "BASEMENT.STEM_WALL_CIP": {
    id: "BASEMENT.STEM_WALL_CIP",
    ...defaultBasementSuperSystemSettings,
    ...defaultStemWallCIPSubSystemSettings,
    foundationConditions: {},
  },
  "SLAB_ON_GRADE.ISOLATED": {
    id: "SLAB_ON_GRADE.ISOLATED",
    ...defaultSlabOnGradeSuperSystemSettings,
    ...defaultConcretePierSubSystemSettings,
    foundationConditions: {},
  },
  "RAISED_SLAB.ISOLATED": {
    id: "RAISED_SLAB.ISOLATED",
    ...defaultRaisedSlabSuperSystemSettings,
    ...defaultConcretePierSubSystemSettings,
    foundationConditions: {},
  },
  "CRAWLSPACE.ISOLATED": {
    id: "CRAWLSPACE.ISOLATED",
    ...defaultCrawlspaceSuperSystemSettings,
    ...defaultConcretePierSubSystemSettings,
    foundationConditions: {},
  },
  "BASEMENT.ISOLATED": {
    id: "BASEMENT.ISOLATED",
    ...defaultBasementSuperSystemSettings,
    ...defaultConcretePierSubSystemSettings,
    foundationConditions: {},
  },
};

const defaultDetailCatalogSettings: DrawingCatalogSettings = {
  type: SettingsType.DRAWING_CATALOG_SETTINGS,
  drawings: defaultDrawingCatalog,
};

const defaultCaseworkCatalog: CaseworkCatalogSettings = {
  type: SettingsType.CASEWORK_CATALOG_SETTINGS,
  caseworkTypes: defaultCaseworkTypes,
};

const defaultCaseworkSettings: CaseworkSettings = {
  type: SettingsType.CASEWORK_SETTINGS,
  //datums
  topOfCountertop: 36,
  bottomOfUpperCabinets: 54,
  bottomOfMicrowaveUpperCabinets: 54,
  bottomOfRefrigeratorUpperCabinets: 70,
  topOfUpperShelf: 87,
  //upper cabinets
  upperCabinetDepth: 12,
  upperMicrowaveCabinetDepth: 15,
  upperRefrigeratorCabinetDepth: 24,
  //base cabinets
  baseCabinetDepth: 24,
  countertopThickness: 1.25,
  countertopOverhang: 1,
  toeKickHeight: 3,
  toeKickDepth: 4,
  //tall cabinets
  tallCabinetDepth: 24,
  //general
  doorStyle: CaseworkDoorStyle.SLAB,
  hardware: true,
  //materials
  paletteType: PaletteType.INTERIOR_PALETTE,
  baseCabinetMaterial: "propDefault0",
  upperCabinetMaterial: "propDefault0",
  tallCabinetMaterial: "propDefault0",
  countertopMaterial: "propDefault0",
  removeFloorMaterialUnderBaseCabinets: false,
};

const defaultDoorCatalogSettings: DoorCatalogSettings = {
  type: SettingsType.DOOR_CATALOG_SETTINGS,
  doorTypes: defaultDoorTypes,
};

const defaultWindowCatalogSettings: WindowCatalogSettings = {
  type: SettingsType.WINDOW_CATALOG_SETTINGS,
  windowTypes: defaultWindowTypes,
};

const defaultElectricalItemCatalogSettings: ElectricalItemCatalogSettings = {
  type: SettingsType.ELECTRICAL_ITEM_CATALOG_SETTINGS,
  loadTypes: defaultLoadCatalog,
};

export const defaultFloorSettings: FloorSettings = {
  type: SettingsType.FLOOR_SETTINGS,
  floorPlateThickness: 12,
  //floorAssemblies: defaultFloorAssembliesPerRoomClass,
  floorAssembly: defaultFrameFloorAssemblyId,
};

export const defaultHeaderFramingSettings: HeaderFramingSettings = {
  type: SettingsType.HEADER_FRAMING_SETTINGS,
  rules: (() => {
    return [
      { maxSpan: worldInchesNew(48), headerAssemblyId: defaultDoubleHeaderAssemblyId2x4, jackStudCount: 1 },
      { maxSpan: worldInchesNew(72), headerAssemblyId: defaultDoubleHeaderAssemblyId2x6, jackStudCount: 1 },
      { maxSpan: worldInchesNew(96), headerAssemblyId: defaultDoubleHeaderAssemblyId2x8, jackStudCount: 1 },
      { maxSpan: worldInchesNew(120), headerAssemblyId: defaultDoubleHeaderAssemblyId2x10, jackStudCount: 1 },
      { maxSpan: worldInchesNew(480), headerAssemblyId: defaultDoubleHeaderAssemblyId2x12, jackStudCount: 1 },
    ];
  })(),
};

const defaultScheduleSettings: ScheduleSettings = {
  type: SettingsType.SCHEDULE_SETTINGS,
  scheduleSettingsTable: {
    DOOR: [DOORS_SCHEDULE_DEFAULT_SETTINGS],
    WINDOW: [WINDOWS_SCHEDULE_DEFAULT_SETTINGS],
    FLOORING: [ROOMS_SCHEDULE_DEFAULT_SETTINGS],
    CASEWORK: [CASEWORK_SCHEDULE_DEFAULT_SETTINGS],
  },
};

export const defaultWindowFrameMaterial: ExteriorMaterial = "glassTrim0";
export const defaultIntegralMullionMaterial: ExteriorMaterial = "glassTrim0";
export const defaultWindowTrimMaterial: ExteriorMaterial = "glassTrim0";
export const defaultWindowVentMaterial: ExteriorMaterial = "trimExterior0";

export const defaultWindowMaterialSettings: WindowMaterialSettings = {
  type: SettingsType.WINDOW_MATERIAL_SETTINGS,
  panelType: WindowPanelType.GLAZING,
  glazingType: GlazingType.STANDARD,
  glazingColor: GlazingColor.CLEAR,
  frameMaterial: defaultWindowFrameMaterial,
  sashMaterial: defaultWindowTrimMaterial,
  gridMaterial: defaultWindowTrimMaterial,
  integralMullionMaterial: defaultIntegralMullionMaterial,
  ventMaterial: defaultWindowVentMaterial,
};

export const defaultHomeSettings: HomeSettings = {
  type: "HOME_SETTINGS",
  name: BuiltinHomeSettingsId.Default,
  exteriorMaterials: defaultExteriorMaterialSettings,
  interiorMaterials: defaultInteriorMaterialSettings,
  structure: defaultStructureSettings,
  interiorProps: defaultInteriorPropSettings,
  exteriorProps: defaultExteriorPropSettings,
  props: {},
  planset: defaultPlanSettings,
  masterPlanset: defaultMasterPlanSettings,
  schedule: defaultScheduleSettings,
  keyMeasures: defaultKeyMeasuresSettings,
  wall: defaultWallSettings,
  wallCatalog: defaultWallCatalogSettings,
  door: defaultDoorSettings,
  window: defaultWindowSettings,
  windowMaterial: defaultWindowMaterialSettings,
  roof: defaultRoofSettings,
  dormer: defaultDormerSettings,
  roofFraming: defaultRoofFramingSettings,
  windowFraming: defaultWindowFramingSettings,
  doorFraming: defaultDoorFramingSettings,
  wainscoting: defaultWainscotingSettings,
  wallFraming: defaultWallFramingSettings,
  headerFraming: defaultHeaderFramingSettings,
  ...defaultFarmhouseWindowSettings,
  awning: defaultAwningSettings,
  porch: defaultPorchSettings,
  exteriorDoorCasing: defaultExteriorDoorCasingSettings,
  interiorDoorCasing: defaultInteriorDoorCasingSettings,
  foundation: defaultFoundationSettings,

  garage: defaultGarageSettings,
  exteriorTrim: defaultExteriorTrimSettings,
  interiorTrim: defaultInteriorTrimSettings,
  electrical: defaultElectricalSettings,
  viewNote: defaultViewNoteSettings,
  profile: defaultProfileSettings,
  column: defaultColumnSettings,
  drawingCatalog: defaultDetailCatalogSettings,
  caseworkCatalog: defaultCaseworkCatalog,
  casework: defaultCaseworkSettings,
  stair: defaultStairCatalogSettings,
  railing: defaultRailingSettings,
  doorCatalog: defaultDoorCatalogSettings,
  windowCatalog: defaultWindowCatalogSettings,
  electricalItemCatalog: defaultElectricalItemCatalogSettings,
  floor: defaultFloorSettings,
  interiorWindowCasing: defaultInteriorWindowCasingSettings,
  windowWrapping: defaultWindowWrappingSettings,
  doorWrapping: defaultDoorWrappingSettings,
};

export const completeDefaultHomeSettings: HomeSettings = {
  name: BuiltinHomeSettingsId.Default,
  ...defaultHomeSettings,
  rearPorch: defaultRearPorchSettings,
};
